
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { QueryNames } from '@/enums/querynames.enum';
import { EventBus } from '@/functions/eventBus';
import Format from '@/functions/format';
import tracking from '@/functions/tracking';
import Query, { Param } from '@/functions/query';
import mSearchTags from '@/components/organisms/search/SearchTags.vue';
import oSearchFilters from '@/components/organisms/search/SearchFilters.vue';
import oSearchSelections from '@/components/organisms/search/SearchSelections.vue';
import oSearchMap from '@/components/organisms/search/SearchMap.vue';
import oSearchList from '@/components/organisms/search/SearchList.vue';
import oSearchAgent from '@/components/organisms/search/SearchAgent.vue';
import oSearchFavoriteProperty from '@/components/organisms/search/SearchFavoriteProperty.vue';
import { ISearchAgent } from '@/interfaces/searchAgent.interface';

import Vue from 'vue';

import SearchFilters, {
    SearchFilter,
    FilterGroup,
    FilterOption,
    Original,
} from '@/models/searchFilters.model';

import {
    SearchListTexts,
    SearchMapTexts,
    SearchFilterTexts,
} from '@/interfaces/searchTexts.interface';

export interface Texts {
    searchFilters: SearchFilterTexts;
    searchMap: SearchMapTexts;
    searchList: SearchListTexts;
}

interface Data {
    groups: FilterGroup[];
    isDesktop: boolean;
}

interface IPropertyFavoriteOverlay {
    redirected: boolean;
    redirectedFrom: string;
    texts: {
        headline: string;
        description: string;
        savedHeadline: string;
        savedDescription: string;
        receiptLinkToProfileName: string;
        receiptLinkToProfileUrl: string;
        loginText: string;
    };
    consent: {
        id: string;
        purposeText: string;
    };
}

export default {
    name: 't-search',
    components: {
        oSearchFilters,
        oSearchSelections,
        oSearchMap,
        oSearchList,
        oSearchAgent,
        oSearchFavoriteProperty,
        mSearchTags,
    },

    props: {
        isLoggedIn: {
            type: Boolean as PropType<boolean>,
            default: () => false,
        },
        redirected: {
            type: Boolean as PropType<boolean>,
            required: true,
            default: () => false,
        },
        redirectedFrom: {
            type: String as PropType<string>,
            required: true,
            default: () => '',
        },
        filterGroups: {
            type: Array as PropType<Original[]>,
            default: () => [],
        },
        orderOptions: {
            type: Array as PropType<{ label: string; value: string }[]>,
            default: () => [],
        },
        texts: {
            type: Object as PropType<Texts>,
            default: () => ({
                searchFilters: {
                    approve: '',
                    reset: '',
                    title: '',
                },
                searchList: {
                    count: '',
                    noResults: '',
                    searchAgentButton: '',
                    zoomOutAndReset: '',
                    openHouseTeaser: '',
                    openHouseTeaserSignup: '',
                },
                searchMap: {
                    close: '',
                    closeMap: '',
                    drawArea: '',
                    helpTextMobile: '',
                    helpTextDesktop: '',
                    noResults: '',
                    removeArea: '',
                    openHouseTeaser: '',
                    openHouseTeaserSignup: '',
                    viewList: '',
                    viewMap: '',
                },
            }),
        },

        searchAgent: {
            isLoggedIn: {
                type: Boolean as PropType<boolean>,
                default: () => false,
            },
            type: Object as PropType<ISearchAgent>,
            default: () => ({
                isLoggedIn: false,
                button: {
                    text: '',
                    icon: '',
                },
                consent: {
                    id: '',
                    displayName: '',
                    heading: '',
                    purposeText: '',
                },
                tooltip: {
                    headline: '',
                    content: '',
                    illegalFiltersText: '',
                    close: '',
                },
                stepOne: {
                    headline: '',
                    content: '',
                    nameLabel: '',
                    namePlaceholder: '',
                    saveButtonText: '',
                    signupLoginButtonText: '',
                },
                // stepTwo id login at Mindworking
                stepThree: {
                    headline: '',
                    content: '',
                    profileLinkUrl: '',
                    profileLinkText: '',
                },
            }),
        },

        propertyFavoriteOverlay: {
            isLoggedIn: {
                type: Boolean as PropType<boolean>,
                default: () => false,
            },
            type: Object as PropType<IPropertyFavoriteOverlay>,
            default: () => ({
                redirected: false,
                redirectedFrom: '',
                texts: {
                    headline: '',
                    description: '',
                    savedHeadline: '',
                    savedDescription: '',
                    receiptLinkToProfileName: '',
                    receiptLinkToProfileUrl: '',
                    loginText: '',
                },
                consent: {
                    id: '',
                    purposeText: '',
                },
            }),
        },
    },

    data(): Data {
        return {
            groups: [],
            isDesktop: EventBus.isDesktop,
        };
    },

    computed: {
        ...mapGetters({
            filters: 'search/filters',
            view: 'search/view',
        }),
        viewTogglerText(): string {
            if (this.view === 'list') {
                return this.texts.searchMap.viewMap;
            }

            return this.isDesktop
                ? this.texts.searchMap.closeMap
                : this.texts.searchMap.viewList;
        },
    },

    beforeMount(): void {
        this.groups = new SearchFilters(this.filterGroups).groups;

        this.loadSearch();

        EventBus.$on('app.resize', () => {
            this.isDesktop = EventBus.isDesktop;
        });

        if (this.view === '') {
            if (this.isDesktop) {
                this.setView('map');
            } else {
                this.setView('list');
            }
        }
    },

    mounted(): void {
        document.querySelector('body')?.classList.add('isSearchPage');
    },

    beforeUnmount(): void {
        EventBus.$off('app.resize');
    },

    methods: {
        ...mapActions({
            filtersLoaded: 'search/filtersLoaded',
            clearFilters: 'search/clearFilters',
            updateFilter: 'search/updateFilter',
            toggleFilter: 'search/toggleFilter',
            setLatitude: 'search/setLatitude',
            setLongitude: 'search/setLongitude',
            setOrder: 'search/setOrder',
            setQuery: 'search/setQuery',
            setZoom: 'search/setZoom',
            setView: 'search/setView',
            toggleView: 'search/toggleView',
            open: 'favoriteProperty/open',
        }),

        toggleMapView(): void {
            this.toggleView();
            if (this.view === 'list') {
                tracking.trackRawEvent({
                    event: 'trackClick',
                    eventData: {
                        category: 'Boligsoegning',
                        action: 'Klik på toggle visning',
                        label: 'Liste visning',
                    },
                });
            }
            if (this.view === 'map' && !this.isDesktop) {
                tracking.trackRawEvent({
                    event: 'trackClick',
                    eventData: {
                        category: 'Boligsoegning',
                        action: 'Klik på toggle visning',
                        label: 'Kort visning',
                    },
                });
            }
            if (this.view === 'map' && this.isDesktop) {
                tracking.trackRawEvent({
                    event: 'trackClick',
                    eventData: {
                        category: 'Boligsoegning',
                        action: 'Klik på toggle visning',
                        label: 'Split visning',
                    },
                });
            }
        },

        /**
         * Load filters from url query parameters.
         *
         * @param {any} state
         * @return {void}
         */
        loadSearch(): void {
            let params = Query.getAll();

            if (!params.length) {
                this.filtersLoaded();

                return;
            }

            this.clearFilters();

            params.forEach((param: Param, paramIndex: number) => {
                if (typeof param.value === 'string') {
                    switch (param.key) {
                        case QueryNames.latitude:
                            this.setLatitude(parseFloat(param.value));
                            break;
                        case QueryNames.longitude:
                            this.setLongitude(parseFloat(param.value));
                            break;
                        case QueryNames.order:
                            this.setOrder(param.value);
                            break;
                        case QueryNames.query:
                            this.setQuery(param.value);
                            break;
                        case QueryNames.zoom:
                            this.setZoom(parseFloat(param.value));
                            break;
                        default:
                            this.setFilter(param);
                            break;
                    }
                }

                if (paramIndex + 1 === params.length) {
                    this.filtersLoaded();
                }
            });
        },

        /**
         * Set filter from url query parameter.
         *
         * @param {Param} param
         * @return {void}
         */
        setFilter(param: Param): void {
            this.groups.forEach((group: any) => {
                const filter: SearchFilter = group.filters.find(
                    (f: SearchFilter) => param.key === f.shortName,
                );
                if (filter) {
                    if (filter.interval) {
                        const value = param.value.toString().split(',');
                        const values: [number, number] = [
                            parseInt(value[0]),
                            parseInt(value[1]),
                        ];
                        const appendValue =
                            filter.max === values[1] && filter.infinityIndicator
                                ? '+'
                                : '';
                        const prependValue =
                            filter.min === values[0] && filter.prepend
                                ? filter.prepend
                                : '';
                        const tagLabelValue = filter.tagLabel
                            ? `${filter.tagLabel}:`
                            : '';

                        let label = '';
                        if (filter.key === 'EnergyLabel') {
                            label = `${tagLabelValue} ${
                                filter.valueMap[values[0]]
                            }-${filter.valueMap[values[1]]}`;
                        } else if (filter.append === 'mio. kr.') {
                            label = `${tagLabelValue} ${prependValue} ${Format.millions(
                                values,
                            )}${appendValue} ${filter.append}`;
                        } else if (filter.append === 'm2') {
                            label = `${tagLabelValue} ${prependValue} ${Format.amount(
                                values[0],
                            )}-${Format.amount(
                                values[1],
                            )}${appendValue} m<sup>2</sup>`;
                        } else if (filter.append) {
                            label = `${tagLabelValue} ${prependValue} ${Format.amount(
                                values[0],
                            )}-${Format.amount(values[1])}${appendValue} ${
                                filter.append
                            }`;
                        } else {
                            label = `${tagLabelValue} ${prependValue} ${values[0]}-${values[1]}${appendValue}`;
                        }

                        this.updateFilter({
                            key: filter.key,
                            label,
                            shortName: filter.shortName,
                            value: values,
                        });
                    } else if (filter.options) {
                        const option = filter.options.find(
                            (o: FilterOption) => o.value === param.value,
                        );

                        if (
                            option &&
                            !this.filters.some(
                                (f: any) =>
                                    f.key === filter.key &&
                                    f.value === option.value,
                            )
                        ) {
                            this.toggleFilter({
                                key: filter.key,
                                label: option.name,
                                shortName: filter.shortName,
                                value: option.value,
                            });
                        }
                    } else {
                        this.updateFilter({
                            key: filter.key,
                            label: param.value,
                            shortName: filter.shortName,
                            value: param.value,
                        });
                    }
                }
            });
        },
    },
};
